import { z } from "zod";
import { BaseTemplateSchema } from "../types/schema";

export const defaultSpectrumConfig = {
    startFreq: 20,
    endFreq: 16000,
    freqBands: 64,
    maxHeight: 240,
    minHeight: 2,
    thickness: 4,
    gap: 2,
    softness: 0,
    insideColor: "#ffffff",
    outsideColor: "#ffffff",
    display: "digital" as const,
    side: "side_ab" as const,
    position: "bottom-center" as const,
};

export const spectrumPresets = {
    bar: {
        display: "digital",
        freqBands: 64,
        thickness: 5,
        softness: 20,
        side: "side_a",
        minHeight: 2,
    },
    line: {
        display: "analog_lines",
        freqBands: 256,
        thickness: 6,
        softness: 80,
        side: "side_a",
    },
    wave: {
        display: "digital",
        freqBands: 256,
        thickness: 2,
        softness: 80,
        side: "side_ab",
    },
    dot: {
        display: "analog_dots",
        freqBands: 128,
        thickness: 3,
        softness: 0,
        side: "side_ab",
    }
} as const;

// Schema definiton with defaults matching the 'bar' preset (default)
export const AudioSpectrumConfigSchema = z.object({
    preset: z.enum(["bar", "line", "wave", "dot"]).default("bar"),
    // Internal field to track preset changes
    lastPreset: z.string().optional(),

    startFreq: z.number().default(defaultSpectrumConfig.startFreq),
    endFreq: z.number().default(defaultSpectrumConfig.endFreq),
    freqBands: z.number().min(1).max(1024).default(defaultSpectrumConfig.freqBands),
    maxHeight: z.number().default(defaultSpectrumConfig.maxHeight),
    minHeight: z.number().default(defaultSpectrumConfig.minHeight),
    thickness: z.number().default(defaultSpectrumConfig.thickness),
    gap: z.number().default(defaultSpectrumConfig.gap),
    softness: z.number().min(0).max(100).default(defaultSpectrumConfig.softness),
    insideColor: z.string().default(defaultSpectrumConfig.insideColor),
    outsideColor: z.string().default(defaultSpectrumConfig.outsideColor),
    display: z.enum(["digital", "analog_lines", "analog_dots"]).default(defaultSpectrumConfig.display),
    side: z.enum(["side_a", "side_b", "side_ab"]).default(defaultSpectrumConfig.side),
    position: z.enum([
        "top-left", "top-center", "top-right",
        "middle-left", "middle-center", "middle-right",
        "bottom-left", "bottom-center", "bottom-right"
    ]).default(defaultSpectrumConfig.position),
});

// Title configuration
export const AudioSpectrumTitleSchema = z.object({
    show: z.boolean().default(true),
    // label removed as it is derived from audioTracks
    position: z.enum(["top", "bottom", "left", "right"]).default("bottom"),
    gap: z.number().default(20),
    style: z.object({
        color: z.string().optional(), // If not set, use insideColor
        fontSize: z.number().default(24),
    }).optional(),
});

export const AudioSpectrumSchema = BaseTemplateSchema.extend({
    spectrum: AudioSpectrumConfigSchema.default({}),
    title: AudioSpectrumTitleSchema.default({}),
    backgroundUrl: z.string().url().default("https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2670&auto=format&fit=crop"),
    isTest: z.boolean().optional(),
});

export type AudioSpectrumProps = z.infer<typeof AudioSpectrumSchema>;
export type AudioSpectrumConfig = z.infer<typeof AudioSpectrumConfigSchema>;

export const defaultAudioSpectrumProps: AudioSpectrumProps = {
    audioTracks: [
        {
            durationInSeconds: 17,
            title: "Synthwave Mix",
            url: "https://cdn.mixetape.com/sample.mp3",
        },
    ],
    backgroundUrl: "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2670&auto=format&fit=crop",
    spectrum: {
        ...defaultSpectrumConfig,
        ...spectrumPresets.bar,
        minHeight: defaultSpectrumConfig.minHeight, // ensure minHeight is included
        preset: "bar",
        lastPreset: "bar",
    },
    title: {
        show: true,
        position: "bottom",
        gap: 20,
    },
};
